import React, { useEffect, useRef } from 'react';
import { SONG_NOTE_STATUS } from '../../../../services/songNotesModel';
import StateChip from './StateChip';
import wireWaDialog from '../wireWaDialog';
import './DeliverDialog.css';

const { FOCUS, LANDED } = SONG_NOTE_STATUS;

/**
 * The Deliver dialog — the ONE gate across the captured→delivered wall
 * (design handoff §Deliver). Delivering a note shows it to the student and
 * their parents, and it CANNOT go back. That irreversibility is the whole
 * reason this is a deliberate dialog and not an inline toggle.
 *
 * Two forward choices, both delivering:
 *   Focus  — "Working on it"  → state focus  (bullseye, primary fill)
 *   Landed — "Nailed it!"     → state landed (check, success-outline)
 * Plus a ghost "Not yet" that closes without delivering.
 *
 * NO PRONOUN ANYWHERE IN THIS COPY (Anthony, 2026-09-27, pass 3 A9). The old
 * labels read "She's working on it" / "She's got it" for every student, and
 * the body said "her parents" whenever a name was known. `students.pronouns`
 * exists, but a first name reads better here than any pronoun, so the body
 * names the student and says "the family". The state word ("Focus",
 * "Landed") stays small beside each label so the dialog still speaks the
 * chip vocabulary. Pinned by DeliverDialog.test.js.
 *
 * wa-dialog controlled the house way: a ref, `.open` driven from React, and a
 * wa-after-hide listener so an ESC/backdrop close resets the gate (React 18
 * doesn't wire custom-element events, so onWaHide would be dead — see the old
 * LessonDashboard.js note this pattern comes from).
 */
/*
 * PASS 3 C4 (2026-09-28): opened from a Carry-forward item as well as from a
 * note row. `context` names the note's song, section and author (a note from
 * another teacher on another song must be confirmed with that in view), and
 * `preselect` marks the choice the item's own button asked for. A note already
 * on Focus is delivered; its only move is to Landed, so the dialog then says
 * "Mark this note landed?", offers only Nailed it!, and drops the "only
 * teachers can see this" warning, which would be false.
 */
const DeliverDialog = ({ note, studentName = 'the student', context = null, preselect = null, onDeliver, onClose }) => {
	const dialogRef = useRef(null);
	const open = Boolean(note);

	// Drive open/close as a property, not an attribute (WA + React 18).
	useEffect(() => {
		if (dialogRef.current) dialogRef.current.open = open;
	}, [open]);

	const deliver = (toStatus) => {
		if (note) onDeliver(note.id, toStatus);
		onClose();
	};

	const who = studentName || 'the student';
	const whoLead = who.charAt(0).toUpperCase() + who.slice(1);
	const alreadyFocus = note?.status === FOCUS;
	const contextLine = [context?.songName, context?.sectionLabel, context?.authorLabel].filter(Boolean).join(' · ');
	const pre = (choice) => (preselect === choice ? ' is-preselected' : '');

	return (
		<wa-dialog
			ref={wireWaDialog(dialogRef, onClose)}
			label={alreadyFocus ? 'Mark this note landed?' : 'Deliver this note?'}
			class="ld-deliver">
			{note && (
				<div className="ld-deliver__body">
					<div className="ld-deliver__preview">
						<div className="ld-deliver__preview-top">
							<StateChip status={alreadyFocus ? FOCUS : SONG_NOTE_STATUS.CAPTURED} />
						</div>
						<div className="ld-deliver__preview-text">{note.text}</div>
						{contextLine && (
							<div className="ld-deliver__context">
								<i className="fa-sharp fa-solid fa-music" aria-hidden="true" />
								{contextLine}
							</div>
						)}
					</div>

					{!alreadyFocus && (
					<div className="ld-deliver__warn">
						<i className="fa-sharp fa-solid fa-eye" aria-hidden="true" />
						<p>
							Right now only teachers can see this. {whoLead} and the family will see
							it. A delivered note cannot be taken back.
						</p>
					</div>
					)}

					<div className="ld-deliver__choices">
						{!alreadyFocus && (
						<button
							type="button"
							className={`ld-deliver__btn ld-deliver__btn--focus${pre(FOCUS)}`}
							aria-pressed={preselect === FOCUS}
							onClick={() => deliver(FOCUS)}>
							<i className="fa-sharp fa-solid fa-bullseye" aria-hidden="true" />
							<span>Working on it</span>
							<span className="ld-deliver__state">Focus</span>
						</button>
						)}
						<button
							type="button"
							className={`ld-deliver__btn ld-deliver__btn--landed${pre(LANDED)}`}
							aria-pressed={preselect === LANDED}
							onClick={() => deliver(LANDED)}>
							<i className="fa-sharp fa-solid fa-circle-check" aria-hidden="true" />
							<span>Nailed it!</span>
							<span className="ld-deliver__state">Landed</span>
						</button>
					</div>
				</div>
			)}

			<button
				type="button"
				slot="footer"
				className="ld-deliver__btn ld-deliver__btn--ghost"
				onClick={onClose}>
				Not yet
			</button>
		</wa-dialog>
	);
};

export default DeliverDialog;
